import React, {useState} from "react";
import {
    Box, Paper, Stack, Typography, Table, TableBody, TableCell, TableHead, TableRow, Link, Accordion, AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {HelpPopover} from "./cells";
import MiniBars from "./MiniBars";
import {fmtMoneyShort, fmtMoneyShort2, fmtPct0, fmtDeltaShort, fmtRange, fmtSignedPct, EMPTY, isNilValue} from "./format";
import {TYPE, TABULAR, NEUTRAL} from "./tokens";

export const NETWORKS_HELP = [
    "Channels = the type of the demand feed that pays each tag (Native, Google search, N2S Yahoo, Yahoo search…); the same rows, in the same order, as the heatmap's first level.",
    "monet./sent = searches the partner counted ÷ searches we sent (grey above 100%: a search yields several pages). $/1k sent = revenue per 1,000 searches we send.",
    "Δ prev = revenue vs the previous range; Δ yday = yesterday vs the network's 7-day base. Both grey when they mislead: the network earned $0 yesterday, half its tags are stopped, or its tags are all new ('new run').",
    "Click a row to filter the table to that network.",
];

/**
 * A network whose week delta misleads: it earned $0 yesterday (Bidberry +161% the week it died), half or more of its tags
 * are STOPPED / DEAD, or all its tags are short-history (a new run — nothing to compare).
 */
const misleading = (g) => (g.rev > 0 && !(g.rev_last > 0)) || (g.tags > 0 && (g.stopped || 0) >= 0.5 * g.tags) || Boolean(g.newRun);

/** Δ colour rule (brief §6): only when |Δ| > 10 and the network's revenue is ≥ 3 % of the total; else grey; misleading → disabled. */
const netDeltaColor = (g, totalRev) => {
    if (!(g.rev_prev > 0) || misleading(g)) return "text.disabled";
    const v = Number(g.delta_rev_pct);
    if (isNilValue(v) || Math.abs(v) <= 10 || !(totalRev > 0) || g.rev / totalRev < 0.03) return "rgba(0,0,0,.38)";
    return v > 0 ? "success.dark" : "error.main";
};

/** Δ yday colour: coloured only when |Δ| > the network's own noise (25 %) and it is worth ≥ 3 % of revenue. */
const ydayColor = (g, totalRev) => {
    if (isNilValue(g.yday_delta_pct) || misleading(g)) return "text.disabled";
    const v = Number(g.yday_delta_pct);
    if (Math.abs(v) <= 25 || !(totalRev > 0) || g.rev / totalRev < 0.03) return "rgba(0,0,0,.38)";
    return v > 0 ? "success.dark" : "error.main";
};

/** `+161%` → `new run` for an all-new network, `—` when nothing earned yesterday. */
const deltaText = (g) => (g.newRun ? "new run" : fmtDeltaShort(g.delta_rev_pct, g.rev_prev, g.rev));

const headSx = {fontSize: 11, fontWeight: 600, color: "text.secondary", borderBottom: `1px solid ${NEUTRAL.divider}`, py: 0.5, px: 1, lineHeight: 1.2, whiteSpace: "nowrap"};
const cellSx = {fontSize: 12.5, py: 0, px: 1, height: 28, borderBottom: `1px solid ${NEUTRAL.hairline}`, ...TABULAR, whiteSpace: "nowrap"};

/** Earning groups shown before the "N more" link (v3: a compact strip). */
const VISIBLE = 5;

const GroupRow = ({g, totalRev, selected, onToggle, zero, dense}) => (
    <TableRow hover selected={selected} onClick={() => onToggle(g.prefix)} role="button" tabIndex={0} aria-pressed={selected}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onToggle(g.prefix); } }}
              sx={{
                  cursor: "pointer", color: zero ? NEUTRAL.idleText : "text.primary",
                  "&.Mui-selected, &.Mui-selected:hover": {bgcolor: NEUTRAL.selected, boxShadow: NEUTRAL.selectedInset},
                  "&:hover": {bgcolor: NEUTRAL.hover},
                  "&:focus-visible": {outline: "none", boxShadow: NEUTRAL.focusRing},
              }}>
        <TableCell sx={{...cellSx, color: "inherit", fontWeight: selected ? 600 : 400, maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis"}}>
            <Box component="span" title={g.code && g.code !== g.label ? `demand feed type ${g.code}` : undefined}>{g.label}</Box>
        </TableCell>
        <TableCell align="right" sx={{...cellSx, color: "text.secondary"}}>{g.tags}</TableCell>
        <TableCell align="right" sx={{...cellSx, fontWeight: 600, color: "inherit"}}>{fmtMoneyShort(g.rev)}</TableCell>
        {!dense && (
            <TableCell sx={{...cellSx, width: 92, px: 0.5}}>
                {g.rev > 0 && Array.isArray(g.daily) && g.daily.length > 0 && (
                    <Box sx={{display: "flex", alignItems: "center", height: "100%"}}>
                        <MiniBars daily={g.daily} width={84} height={16} gap={g.daily.length > 14 ? 1 : 2} label={`${g.label}: daily revenue`}/>
                    </Box>
                )}
            </TableCell>
        )}
        <TableCell align="right" title={misleading(g) ? "week and day disagree: the network earned $0 yesterday, half its tags stopped, or all its tags are new" : undefined}
                   sx={{...cellSx, fontWeight: 600, color: netDeltaColor(g, totalRev)}}>{deltaText(g)}</TableCell>
        <TableCell align="right" sx={{...cellSx, fontWeight: 600, color: ydayColor(g, totalRev)}}>{isNilValue(g.yday_delta_pct) ? EMPTY : fmtSignedPct(g.yday_delta_pct)}</TableCell>
        <TableCell align="right" sx={{...cellSx, color: g.coverage > 1 ? "text.disabled" : "text.secondary"}} title={g.coverage > 1 ? "Over 100%: a search yields several monetized pages" : undefined}>{g.fs > 0 ? fmtPct0(g.coverage) : EMPTY}</TableCell>
        <TableCell align="right" sx={{...cellSx, color: "inherit"}}>{fmtMoneyShort2(g.rpm_fs)}</TableCell>
    </TableRow>
);

/** `dense` (phone accordion): no bars column and no min width so the table fits 375 px without a horizontal scroll. */
const GroupsTable = ({groups, selected, onToggle, dense, range}) => {
    const [showZero, setShowZero] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const earning = groups.filter((g) => g.rev > 0);
    const zero = groups.filter((g) => !(g.rev > 0));
    const totalRev = groups.reduce((s, g) => s + (g.rev || 0), 0);
    const zeroVisible = showZero || zero.some((g) => selected.includes(g.prefix));
    // Selected groups past the cap stay visible so a filter never hides its own row.
    // A single overflow row is not worth a link: show up to VISIBLE + 1.
    const allVisible = showAll || earning.length <= VISIBLE + 1 || earning.slice(VISIBLE).some((g) => selected.includes(g.prefix));
    const shown = allVisible ? earning : earning.slice(0, VISIBLE);
    const hiddenEarning = earning.length - shown.length;
    const colSpan = dense ? 7 : 8;
    return (
        <Box sx={{overflowX: "auto", mx: dense ? 0 : -1.5}}>
            <Table size="small" sx={{minWidth: dense ? 0 : 420, "& .MuiTableCell-root": {borderBottom: 0}}} aria-label="Channels">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{...headSx, pl: 1.5}}>Channel</TableCell>
                        <TableCell align="right" sx={headSx}>Tags</TableCell>
                        <TableCell align="right" sx={headSx}>Revenue</TableCell>
                        {!dense && <TableCell sx={{...headSx, px: 0.5}} title="Daily revenue of the network over the range">{range ? fmtRange(range.from, range.to) : "Daily"}</TableCell>}
                        <TableCell align="right" sx={headSx} title="Revenue vs the previous range (grey when it misleads)">Δ prev</TableCell>
                        <TableCell align="right" sx={headSx} title="Yesterday vs the network's 7-day base">Δ yday</TableCell>
                        <TableCell align="right" sx={headSx} title="Searches the partner counted ÷ searches we sent (grey above 100%: a search yields several pages)">monet./sent</TableCell>
                        <TableCell align="right" sx={{...headSx, pr: 1.5}} title="Revenue per 1,000 searches sent">$/1k sent</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {shown.map((g) => <GroupRow key={g.prefix} g={g} totalRev={totalRev} selected={selected.includes(g.prefix)} onToggle={onToggle} dense={dense}/>)}
                    {(hiddenEarning > 0 || (showAll && earning.length > VISIBLE)) && (
                        <TableRow>
                            <TableCell colSpan={colSpan} sx={{...cellSx, height: 26, borderBottom: 0, pl: 1.5, fontSize: 11.5}}>
                                <Link component="button" type="button" underline="hover" onClick={() => setShowAll((v) => !v)}
                                      aria-expanded={allVisible} sx={{fontSize: "inherit", fontFamily: "inherit", verticalAlign: "baseline"}}>
                                    {hiddenEarning > 0 ? `${hiddenEarning} more` : "Show fewer"}
                                </Link>
                            </TableCell>
                        </TableRow>
                    )}
                    {zeroVisible && zero.map((g) => <GroupRow key={g.prefix} g={g} totalRev={totalRev} selected={selected.includes(g.prefix)} onToggle={onToggle} zero dense={dense}/>)}
                    {zero.length > 0 && (
                        <TableRow>
                            <TableCell colSpan={colSpan} sx={{...cellSx, height: 26, borderBottom: 0, color: "text.secondary", pl: 1.5, fontSize: 11.5}}>
                                {zero.length} channel{zero.length === 1 ? "" : "s"} with $0
                                <Box component="span" sx={{color: "text.disabled"}}> ({zero.slice(0, 6).map((g) => g.prefix).join(", ")}{zero.length > 6 ? ", …" : ""})</Box>
                                {" "}
                                <Link component="button" type="button" underline="hover" onClick={() => setShowZero((v) => !v)} sx={{fontSize: "inherit", fontFamily: "inherit", verticalAlign: "baseline"}}>
                                    {zeroVisible ? "hide" : "show"}
                                </Link>
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </Box>
    );
};

/**
 * Supply networks panel (tags grouped by prefix). `groups` = rollups (rev desc, with summed `daily`); `selected` = prefixes
 * in the filter; `onToggle(prefix)`. Phone: collapsed Accordion.
 */
const GroupsPanel = ({groups, selected, onToggle, phone = false, range}) => {
    const earning = groups.filter((g) => g.rev > 0).length;
    const zero = groups.length - earning;
    const header = (
        <Stack direction="row" alignItems="baseline" spacing={1} sx={{minWidth: 0, flex: "1 1 auto"}}>
            <Typography component="h2" sx={{...TYPE.eyebrow, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap"}}>Channels</Typography>
            <Typography component="span" noWrap sx={{fontSize: 12.5, color: "text.secondary", ...TABULAR, minWidth: 0}}>
                by demand feed type · {earning} earning{zero ? ` · ${zero} with $0` : ""}{selected.length ? ` · ${selected.length} selected` : ""}
            </Typography>
        </Stack>
    );
    if (phone) {
        return (
            <Accordion disableGutters elevation={0} square sx={{borderTop: `1px solid ${NEUTRAL.hairline}`, borderBottom: `1px solid ${NEUTRAL.hairline}`, "&::before": {display: "none"}}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon/>} sx={{minHeight: 40, px: 1.5, "& .MuiAccordionSummary-content": {my: 0.75}}}>
                    {header}
                </AccordionSummary>
                <AccordionDetails sx={{p: 0, pb: 1}}>
                    <GroupsTable groups={groups} selected={selected} onToggle={onToggle} dense range={range}/>
                </AccordionDetails>
            </Accordion>
        );
    }
    return (
        <Paper variant="outlined" sx={{borderRadius: 1.5, p: 1.5, pt: 1.25, minWidth: 0}}>
            <Stack direction="row" alignItems="center" sx={{mb: 0.5, minWidth: 0}}>
                {header}
                <HelpPopover label="Channels" lines={NETWORKS_HELP}/>
            </Stack>
            <GroupsTable groups={groups} selected={selected} onToggle={onToggle} range={range}/>
        </Paper>
    );
};

export default GroupsPanel;
